import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  TrendingUp,
  DollarSign,
  Eye,
  Users,
  Calendar,
  Plus,
  Upload,
  BarChart3,
  CreditCard,
  UserPlus,
  Trophy,
  Database,
  Video,
  ChevronRight,
  Trash2,
  Edit,
  Loader2,
  Shield,
  GitBranch,
  MapPin,
  Clock,
  Building,
  RefreshCw,
  Radio,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { user, token, isAdmin, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('analytics');
  
  // Real data from backend
  const [teams, setTeams] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [eventStreams, setEventStreams] = useState([]);
  
  // Team form state
  const [showTeamDialog, setShowTeamDialog] = useState(false);
  const [teamForm, setTeamForm] = useState({ name: '', sport: 'Basketball', coach_name: '', coach_email: '', color: '#00d4ff' });
  
  // Player form state
  const [showPlayerDialog, setShowPlayerDialog] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [playerForm, setPlayerForm] = useState({ name: '', jersey_number: '', position: '', grade: '', height: '' });
  
  // Tournament form state
  const [showTournamentDialog, setShowTournamentDialog] = useState(false);
  const [tournamentForm, setTournamentForm] = useState({
    name: '',
    sport: 'Basketball',
    bracket_type: 'single',
    num_teams: 8,
    teams: '',
    auto_create_streams: true,
    start_date: '',
    venue_id: '',
    // Pool play options
    num_pools: 2,
    teams_advance_per_pool: 2,
    playoff_bracket_type: 'single',
    seeding_method: 'cross_pool',
    pool_assignments: ''
  });
  
  // Match score update
  const [showScoreDialog, setShowScoreDialog] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [scoreForm, setScoreForm] = useState({ team1_score: 0, team2_score: 0 });
  
  // Schedule dialog
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    scheduled_date: '',
    scheduled_time: '',
    venue_id: '',
    court_id: ''
  });

  // Mock revenue data (would come from Stripe in production)
  const revenueData = {
    today: { revenue: 156.42, eCPM: 7.50, impressions: 20856, clicks: 89 },
    yesterday: { revenue: 142.31, eCPM: 7.45, impressions: 19110, clicks: 76 },
    thisMonth: { revenue: 3842.15, eCPM: 7.48, impressions: 513620, clicks: 2341 },
    lastMonth: { revenue: 3567.89, eCPM: 7.52, impressions: 474520, clicks: 2103 },
  };

  // Pull to refresh state
  const [refreshing, setRefreshing] = useState(false);
  const [touchStart, setTouchStart] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [teamsRes, tournamentsRes, venuesRes] = await Promise.all([
        fetch(`${API_URL}/api/teams`),
        fetch(`${API_URL}/api/tournaments`),
        fetch(`${API_URL}/api/venues`)
      ]);
      
      if (teamsRes.ok) {
        const teamsData = await teamsRes.json();
        setTeams(teamsData);
      }
      
      if (tournamentsRes.ok) {
        const tournamentsData = await tournamentsRes.json();
        setTournaments(tournamentsData);
      }
      
      if (venuesRes.ok) {
        const venuesData = await venuesRes.json();
        setVenues(venuesData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setRefreshing(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Pull to refresh handlers
  const handleTouchStart = (e) => {
    if (window.scrollY === 0) {
      setTouchStart(e.touches[0].clientY);
    }
  };

  const handleTouchMove = (e) => {
    if (!touchStart) return;
    const touchEnd = e.touches[0].clientY;
    const diff = touchEnd - touchStart;
    
    if (diff > 80 && !refreshing) {
      setRefreshing(true);
      loadData();
    }
  };

  const handleTouchEnd = () => {
    setTouchStart(null);
  };

  const fetchTeams = async () => {
    try {
      const res = await fetch(`${API_URL}/api/teams`);
      if (res.ok) {
        const data = await res.json();
        setTeams(data);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const fetchTournaments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tournaments`);
      if (res.ok) {
        const data = await res.json();
        setTournaments(data);
      }
    } catch (error) {
      console.error('Error fetching tournaments:', error);
    }
  };

  const fetchEventStreams = async () => {
    try {
      const eventsRes = await fetch(`${API_URL}/api/events`);
      if (!eventsRes.ok) return;
      const events = await eventsRes.json();
      
      const streamsData = await Promise.all(events.map(async (event) => {
        try {
          const configRes = await fetch(`${API_URL}/api/court-streams/event/${event.id || event.event_id}/config`);
          const config = configRes.ok ? await configRes.json() : { stream_mode: 'per_game', courts: [] };
          
          let courts = [];
          if (config.stream_mode === 'per_court') {
            const courtsRes = await fetch(`${API_URL}/api/court-streams/event/${event.id || event.event_id}`);
            courts = courtsRes.ok ? await courtsRes.json() : [];
          }
          
          return { ...event, stream_config: config, court_streams: courts };
        } catch {
          return { ...event, stream_config: { stream_mode: 'per_game' }, court_streams: [] };
        }
      }));
      
      setEventStreams(streamsData);
    } catch (err) {
      console.error('Error fetching event streams:', err);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/teams`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(teamForm)
      });
      
      if (res.ok) {
        const newTeam = await res.json();
        setTeams([...teams, newTeam]);
        setShowTeamDialog(false);
        setTeamForm({ name: '', sport: 'Basketball', coach_name: '', coach_email: '', color: '#00d4ff' });
        toast.success('Team created successfully!');
      } else {
        toast.error('Failed to create team');
      }
    } catch (error) {
      toast.error('Error creating team');
    }
    setLoading(false);
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/teams/${teamId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setTeams(teams.filter(t => t.id !== teamId));
        toast.success('Team deleted');
      } else {
        toast.error('Failed to delete team');
      }
    } catch (error) {
      toast.error('Error deleting team');
    }
  };

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    if (!selectedTeamId) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/teams/${selectedTeamId}/players`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...playerForm,
          jersey_number: parseInt(playerForm.jersey_number)
        })
      });
      
      if (res.ok) {
        const updatedTeam = await res.json();
        setTeams(teams.map(t => t.id === selectedTeamId ? updatedTeam : t));
        setShowPlayerDialog(false);
        setPlayerForm({ name: '', jersey_number: '', position: '', grade: '', height: '' });
        toast.success('Player added successfully!');
      } else {
        toast.error('Failed to add player');
      }
    } catch (error) {
      toast.error('Error adding player');
    }
    setLoading(false);
  };

  const handleRemovePlayer = async (teamId, playerId) => {
    try {
      const res = await fetch(`${API_URL}/api/teams/${teamId}/players/${playerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setTeams(teams.map(t => {
          if (t.id === teamId) {
            return { ...t, players: t.players.filter(p => p.id !== playerId) };
          }
          return t;
        }));
        toast.success('Player removed');
      }
    } catch (error) {
      toast.error('Error removing player');
    }
  };

  const handleCreateTournament = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    try {
      const teamsArray = tournamentForm.teams
        ? tournamentForm.teams.split(',').map(t => t.trim()).filter(t => t)
        : null;
      
      // Build request body
      const requestBody = {
        name: tournamentForm.name,
        sport: tournamentForm.sport,
        bracket_type: tournamentForm.bracket_type,
        num_teams: tournamentForm.bracket_type === 'singlegame' ? 2 : parseInt(tournamentForm.num_teams),
        teams: teamsArray,
        auto_create_streams: tournamentForm.auto_create_streams
      };
      
      // Add pool play options if applicable
      if (tournamentForm.bracket_type === 'poolplay') {
        requestBody.num_pools = parseInt(tournamentForm.num_pools);
        requestBody.teams_advance_per_pool = parseInt(tournamentForm.teams_advance_per_pool);
        requestBody.playoff_bracket_type = tournamentForm.playoff_bracket_type;
        requestBody.seeding_method = tournamentForm.seeding_method;
        
        // Parse manual pool assignments if provided
        if (tournamentForm.pool_assignments) {
          try {
            requestBody.pool_assignments = JSON.parse(tournamentForm.pool_assignments);
          } catch {
            // Ignore parse errors, will use auto-assignment
          }
        }
      }
      
      const res = await fetch(`${API_URL}/api/tournaments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      if (res.ok) {
        const newTournament = await res.json();
        setTournaments([...tournaments, newTournament]);
        setShowTournamentDialog(false);
        setTournamentForm({ 
          name: '', sport: 'Basketball', bracket_type: 'single', num_teams: 8, teams: '', 
          auto_create_streams: true, num_pools: 2, teams_advance_per_pool: 2, 
          playoff_bracket_type: 'single', seeding_method: 'cross_pool', pool_assignments: ''
        });
        
        let successMsg = `Tournament created`;
        if (newTournament.pool_matches) {
          successMsg += ` with ${newTournament.pool_matches} pool games and ${newTournament.bracket_matches} bracket games`;
        }
        if (newTournament.streams_created) {
          successMsg += ` (${newTournament.streams_created} streams ready)`;
        }
        toast.success(successMsg);
      } else {
        toast.error('Failed to create tournament');
      }
    } catch (error) {
      toast.error('Error creating tournament');
    }
    setLoading(false);
  };

  const handleUpdateMatchScore = async (e) => {
    e.preventDefault();
    if (!selectedTournament || !selectedMatch) return;
    
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/tournaments/${selectedTournament.id}/matches/${selectedMatch.id}/score`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            team1_score: parseInt(scoreForm.team1_score),
            team2_score: parseInt(scoreForm.team2_score),
            status: 'completed'
          })
        }
      );
      
      if (res.ok) {
        await fetchTournaments();
        setShowScoreDialog(false);
        setSelectedMatch(null);
        setSelectedTournament(null);
        toast.success('Score updated!');
      } else {
        toast.error('Failed to update score');
      }
    } catch (error) {
      toast.error('Error updating score');
    }
    setLoading(false);
  };

  const handleScheduleMatch = async (e) => {
    e.preventDefault();
    if (!selectedTournament || !selectedMatch) return;
    
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/tournaments/${selectedTournament.id}/matches/${selectedMatch.id}/schedule`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(scheduleForm)
        }
      );
      
      if (res.ok) {
        await fetchTournaments();
        setShowScheduleDialog(false);
        setSelectedMatch(null);
        setSelectedTournament(null);
        toast.success('Match scheduled!');
      } else {
        toast.error('Failed to schedule match');
      }
    } catch (error) {
      toast.error('Error scheduling match');
    }
    setLoading(false);
  };

  const handleAutoSchedule = async (tournamentId) => {
    if (venues.length === 0) {
      toast.error('Please add a venue first');
      return;
    }
    
    const venue = venues[0];
    const today = new Date().toISOString().split('T')[0];
    
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/tournaments/${tournamentId}/auto-schedule?venue_id=${venue.id}&start_date=${today}&start_time=09:00`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (res.ok) {
        await fetchTournaments();
        toast.success('Tournament auto-scheduled!');
      } else {
        toast.error('Failed to auto-schedule');
      }
    } catch (error) {
      toast.error('Error auto-scheduling');
    }
    setLoading(false);
  };

  const handleDeleteTournament = async (tournamentId) => {
    if (!window.confirm('Are you sure you want to delete this tournament?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/tournaments/${tournamentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setTournaments(tournaments.filter(t => t.id !== tournamentId));
        toast.success('Tournament deleted');
      } else {
        toast.error('Failed to delete tournament');
      }
    } catch (error) {
      toast.error('Error deleting tournament');
    }
  };

  const handlePayment = async (packageId) => {
    if (!isAuthenticated()) {
      toast.error('Please login to make a purchase');
      navigate('/login');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin,
          metadata: {
            user_id: user?.id,
            user_email: user?.email
          }
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.url;
      } else {
        toast.error('Failed to initiate payment');
      }
    } catch (error) {
      toast.error('Error processing payment');
    }
  };

  const getBracketTypeLabel = (type) => {
    switch(type) {
      case 'single': return 'Single Elimination';
      case 'double': return 'Double Elimination';
      case 'roundrobin': return 'Round Robin';
      case 'singlegame': return 'Single Game';
      case 'poolplay': return 'Pool Play → Bracket';
      default: return type;
    }
  };

  const getStatusBadge = (tournament) => {
    const status = tournament.status;
    if (status === 'pool_play') {
      return { className: 'bg-blue-500/20 text-blue-400 border-blue-500', label: 'Pool Play' };
    } else if (status === 'bracket_play') {
      return { className: 'bg-purple-500/20 text-purple-400 border-purple-500', label: 'Bracket Play' };
    } else if (status === 'completed') {
      return { className: 'bg-green-500/20 text-green-400 border-green-500', label: 'Completed' };
    }
    return { className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500', label: status };
  };

  return (
    <div 
      className="min-h-screen bg-rooam-black"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull to refresh indicator */}
      {refreshing && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 bg-rooam-blue text-black px-4 py-2 rounded-full flex items-center gap-2">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Refreshing...
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        {/* Header */}
        <div className="mb-6 md:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-1 sm:mb-2">Creator Dashboard</h1>
            <p className="text-sm sm:text-base text-gray-400">
              Track your revenue, manage events, rosters, and tournament brackets
            </p>
          </div>
          {isAdmin() && (
            <Button 
              className="bg-rooam-red hover:bg-red-600 text-white font-semibold w-full sm:w-auto"
              onClick={() => navigate('/admin')}
            >
              <Shield className="h-4 w-4 mr-2" />
              Admin Panel
            </Button>
          )}
        </div>

        {/* Admin Quick Access */}
        <Card className="bg-gradient-to-r from-rooam-blue/20 to-rooam-gray-medium border-rooam-blue/50 mb-6 md:mb-8">
          <CardContent className="p-4 md:p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="bg-rooam-blue/30 p-3 rounded-xl">
                  <Database className="h-8 w-8 text-rooam-blue" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Game Storage Manager</h3>
                  <p className="text-gray-400">Manage game recordings, replays, and video storage</p>
                </div>
              </div>
              <Button className="bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold" asChild>
                <Link to="/storage">
                  <Database className="h-4 w-4 mr-2" />
                  Manage Storage
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 md:space-y-6">
          <TabsList className="bg-rooam-gray-medium border border-rooam-gray-light w-full overflow-x-auto flex-nowrap justify-start sm:justify-center">
            <TabsTrigger value="analytics" className="data-[state=active]:bg-rooam-blue data-[state=active]:text-black text-xs sm:text-sm whitespace-nowrap">
              <BarChart3 className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Analytics</span>
              <span className="sm:hidden">Stats</span>
            </TabsTrigger>
            <TabsTrigger value="teams" className="data-[state=active]:bg-rooam-blue data-[state=active]:text-black text-xs sm:text-sm whitespace-nowrap">
              <Users className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Team Rosters</span>
              <span className="sm:hidden">Teams</span>
            </TabsTrigger>
            <TabsTrigger value="tournaments" className="data-[state=active]:bg-rooam-blue data-[state=active]:text-black text-xs sm:text-sm whitespace-nowrap">
              <Trophy className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Tournaments</span>
              <span className="sm:hidden">Brackets</span>
            </TabsTrigger>
            <TabsTrigger value="payments" className="data-[state=active]:bg-rooam-blue data-[state=active]:text-black text-xs sm:text-sm whitespace-nowrap">
              <CreditCard className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Payments</span>
              <span className="sm:hidden">Pay</span>
            </TabsTrigger>
            <TabsTrigger value="streams" className="data-[state=active]:bg-rooam-blue data-[state=active]:text-black text-xs sm:text-sm whitespace-nowrap" onClick={fetchEventStreams}>
              <Radio className="h-4 w-4 mr-1 sm:mr-2" />
              Streams
            </TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
              <Card className="bg-rooam-gray-medium border-rooam-gray-light">
                <CardContent className="p-3 sm:p-6">
                  <div className="flex items-center justify-between mb-1 sm:mb-2">
                    <span className="text-xs sm:text-sm text-gray-400">Today&apos;s Revenue</span>
                    <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
                  </div>
                  <div className="text-xl sm:text-3xl font-bold text-white">${revenueData.today.revenue}</div>
                  <p className="text-xs text-gray-500 mt-1 hidden sm:block">
                    eCPM: ${revenueData.today.eCPM}
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-rooam-gray-medium border-rooam-gray-light">
                <CardContent className="p-3 sm:p-6">
                  <div className="flex items-center justify-between mb-1 sm:mb-2">
                    <span className="text-xs sm:text-sm text-gray-400">Impressions</span>
                    <Eye className="h-4 w-4 sm:h-5 sm:w-5 text-rooam-blue" />
                  </div>
                  <div className="text-xl sm:text-3xl font-bold text-white">
                    {(revenueData.today.impressions / 1000).toFixed(0)}K
                  </div>
                  <p className="text-xs text-gray-500 mt-1 hidden sm:block">Today</p>
                </CardContent>
              </Card>
              
              <Card className="bg-rooam-gray-medium border-rooam-gray-light">
                <CardContent className="p-3 sm:p-6">
                  <div className="flex items-center justify-between mb-1 sm:mb-2">
                    <span className="text-xs sm:text-sm text-gray-400">This Month</span>
                    <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
                  </div>
                  <div className="text-xl sm:text-3xl font-bold text-white">${revenueData.thisMonth.revenue}</div>
                  <p className="text-xs text-green-400 mt-1 hidden sm:block">
                    +{((revenueData.thisMonth.revenue - revenueData.lastMonth.revenue) / revenueData.lastMonth.revenue * 100).toFixed(1)}%
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-rooam-gray-medium border-rooam-gray-light">
                <CardContent className="p-3 sm:p-6">
                  <div className="flex items-center justify-between mb-1 sm:mb-2">
                    <span className="text-xs sm:text-sm text-gray-400">Total Teams</span>
                    <Users className="h-4 w-4 sm:h-5 sm:w-5 text-purple-400" />
                  </div>
                  <div className="text-xl sm:text-3xl font-bold text-white">{teams.length}</div>
                  <p className="text-xs text-gray-500 mt-1 hidden sm:block">Active rosters</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Teams Tab */}
          <TabsContent value="teams">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-white">Team Rosters</h2>
              <Dialog open={showTeamDialog} onOpenChange={setShowTeamDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Team
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light">
                  <DialogHeader>
                    <DialogTitle className="text-white">Create New Team</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateTeam} className="space-y-4">
                    <div>
                      <Label className="text-gray-300">Team Name</Label>
                      <Input
                        value={teamForm.name}
                        onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                        className="bg-rooam-black border-rooam-gray-light text-white"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Sport</Label>
                      <Select value={teamForm.sport} onValueChange={(v) => setTeamForm({ ...teamForm, sport: v })}>
                        <SelectTrigger className="bg-rooam-black border-rooam-gray-light text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                          <SelectItem value="Basketball">Basketball</SelectItem>
                          <SelectItem value="Football">Football</SelectItem>
                          <SelectItem value="Soccer">Soccer</SelectItem>
                          <SelectItem value="Volleyball">Volleyball</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-gray-300">Coach Name</Label>
                      <Input
                        value={teamForm.coach_name}
                        onChange={(e) => setTeamForm({ ...teamForm, coach_name: e.target.value })}
                        className="bg-rooam-black border-rooam-gray-light text-white"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Coach Email</Label>
                      <Input
                        type="email"
                        value={teamForm.coach_email}
                        onChange={(e) => setTeamForm({ ...teamForm, coach_email: e.target.value })}
                        className="bg-rooam-black border-rooam-gray-light text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Team Color</Label>
                      <Input
                        type="color"
                        value={teamForm.color}
                        onChange={(e) => setTeamForm({ ...teamForm, color: e.target.value })}
                        className="h-10 cursor-pointer"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-rooam-blue hover:bg-rooam-blue-light text-black" disabled={loading}>
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Team'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {teams.length === 0 ? (
              <Card className="bg-rooam-gray-medium border-rooam-gray-light">
                <CardContent className="p-8 sm:p-12 text-center">
                  <Users className="h-12 w-12 sm:h-16 sm:w-16 text-gray-500 mx-auto mb-4" />
                  <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">No Teams Yet</h3>
                  <p className="text-sm sm:text-base text-gray-400 mb-4">Create your first team to start managing rosters</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                {teams.map((team) => (
                  <Card key={team.id} className="bg-rooam-gray-medium border-rooam-gray-light">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: team.color || '#00d4ff' }}
                        />
                        <CardTitle className="text-white">{team.name}</CardTitle>
                        <Badge className="bg-rooam-blue/20 text-rooam-blue border-rooam-blue">
                          {team.sport}
                        </Badge>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-rooam-blue hover:bg-rooam-blue/10"
                          onClick={() => {
                            setSelectedTeamId(team.id);
                            setShowPlayerDialog(true);
                          }}
                        >
                          <UserPlus className="h-4 w-4" />
                        </Button>
                        {isAdmin() && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:bg-red-500/10"
                            onClick={() => handleDeleteTeam(team.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-400 mb-4">Coach: {team.coach_name}</p>
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-gray-300">
                          Players ({team.players?.length || 0})
                        </p>
                        {team.players?.length > 0 ? (
                          <div className="max-h-40 overflow-y-auto space-y-1">
                            {team.players.map((player) => (
                              <div key={player.id} className="flex items-center justify-between bg-rooam-black p-2 rounded">
                                <span className="text-white">
                                  #{player.jersey_number} {player.name}
                                  {player.position && <span className="text-gray-400 text-sm ml-2">({player.position})</span>}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-400 hover:bg-red-500/10 h-6 w-6 p-0"
                                  onClick={() => handleRemovePlayer(team.id, player.id)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500">No players added yet</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Add Player Dialog */}
            <Dialog open={showPlayerDialog} onOpenChange={setShowPlayerDialog}>
              <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light">
                <DialogHeader>
                  <DialogTitle className="text-white">Add Player</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddPlayer} className="space-y-4">
                  <div>
                    <Label className="text-gray-300">Player Name</Label>
                    <Input
                      value={playerForm.name}
                      onChange={(e) => setPlayerForm({ ...playerForm, name: e.target.value })}
                      className="bg-rooam-black border-rooam-gray-light text-white"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-300">Jersey #</Label>
                      <Input
                        type="number"
                        value={playerForm.jersey_number}
                        onChange={(e) => setPlayerForm({ ...playerForm, jersey_number: e.target.value })}
                        className="bg-rooam-black border-rooam-gray-light text-white"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Position</Label>
                      <Input
                        value={playerForm.position}
                        onChange={(e) => setPlayerForm({ ...playerForm, position: e.target.value })}
                        className="bg-rooam-black border-rooam-gray-light text-white"
                        placeholder="e.g., PG, SF"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-300">Grade</Label>
                      <Input
                        value={playerForm.grade}
                        onChange={(e) => setPlayerForm({ ...playerForm, grade: e.target.value })}
                        className="bg-rooam-black border-rooam-gray-light text-white"
                        placeholder="e.g., Senior"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Height</Label>
                      <Input
                        value={playerForm.height}
                        onChange={(e) => setPlayerForm({ ...playerForm, height: e.target.value })}
                        className="bg-rooam-black border-rooam-gray-light text-white"
                        placeholder='e.g., 6&apos;2"'
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-rooam-blue hover:bg-rooam-blue-light text-black" disabled={loading}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Player'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Tournaments Tab */}
          <TabsContent value="tournaments">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-white">Tournament Brackets</h2>
              <Dialog open={showTournamentDialog} onOpenChange={setShowTournamentDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Tournament
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light">
                  <DialogHeader>
                    <DialogTitle className="text-white">Create Tournament</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateTournament} className="space-y-4">
                    <div>
                      <Label className="text-gray-300">Tournament Name</Label>
                      <Input
                        value={tournamentForm.name}
                        onChange={(e) => setTournamentForm({ ...tournamentForm, name: e.target.value })}
                        className="bg-rooam-black border-rooam-gray-light text-white"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Sport</Label>
                      <Select value={tournamentForm.sport} onValueChange={(v) => setTournamentForm({ ...tournamentForm, sport: v })}>
                        <SelectTrigger className="bg-rooam-black border-rooam-gray-light text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                          <SelectItem value="Basketball">Basketball</SelectItem>
                          <SelectItem value="Football">Football</SelectItem>
                          <SelectItem value="Soccer">Soccer</SelectItem>
                          <SelectItem value="Volleyball">Volleyball</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-gray-300">Bracket Type</Label>
                      <Select value={tournamentForm.bracket_type} onValueChange={(v) => setTournamentForm({ ...tournamentForm, bracket_type: v })}>
                        <SelectTrigger className="bg-rooam-black border-rooam-gray-light text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                          <SelectItem value="singlegame">Single Game Matchup</SelectItem>
                          <SelectItem value="single">Single Elimination</SelectItem>
                          <SelectItem value="double">Double Elimination</SelectItem>
                          <SelectItem value="roundrobin">Round Robin</SelectItem>
                          <SelectItem value="poolplay">Pool Play → Bracket</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {tournamentForm.bracket_type !== 'singlegame' && (
                      <div>
                        <Label className="text-gray-300">Number of Teams</Label>
                        <Select value={String(tournamentForm.num_teams)} onValueChange={(v) => setTournamentForm({ ...tournamentForm, num_teams: parseInt(v) })}>
                          <SelectTrigger className="bg-rooam-black border-rooam-gray-light text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                            <SelectItem value="4">4 Teams</SelectItem>
                            <SelectItem value="8">8 Teams</SelectItem>
                            <SelectItem value="12">12 Teams</SelectItem>
                            <SelectItem value="16">16 Teams</SelectItem>
                            <SelectItem value="24">24 Teams</SelectItem>
                            <SelectItem value="32">32 Teams</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    {/* Pool Play Options */}
                    {tournamentForm.bracket_type === 'poolplay' && (
                      <div className="space-y-4 p-4 bg-rooam-black rounded-lg border border-rooam-blue/30">
                        <p className="text-sm font-medium text-rooam-blue">Pool Play Configuration</p>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-gray-300">Number of Pools</Label>
                            <Select 
                              value={String(tournamentForm.num_pools)} 
                              onValueChange={(v) => setTournamentForm({ ...tournamentForm, num_pools: parseInt(v) })}
                            >
                              <SelectTrigger className="bg-rooam-gray-medium border-rooam-gray-light text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                                <SelectItem value="2">2 Pools</SelectItem>
                                <SelectItem value="4">4 Pools</SelectItem>
                                <SelectItem value="6">6 Pools</SelectItem>
                                <SelectItem value="8">8 Pools</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-gray-300">Teams Advance/Pool</Label>
                            <Select 
                              value={String(tournamentForm.teams_advance_per_pool)} 
                              onValueChange={(v) => setTournamentForm({ ...tournamentForm, teams_advance_per_pool: parseInt(v) })}
                            >
                              <SelectTrigger className="bg-rooam-gray-medium border-rooam-gray-light text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                                <SelectItem value="1">Top 1</SelectItem>
                                <SelectItem value="2">Top 2</SelectItem>
                                <SelectItem value="3">Top 3</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-gray-300">Playoff Bracket</Label>
                            <Select 
                              value={tournamentForm.playoff_bracket_type} 
                              onValueChange={(v) => setTournamentForm({ ...tournamentForm, playoff_bracket_type: v })}
                            >
                              <SelectTrigger className="bg-rooam-gray-medium border-rooam-gray-light text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                                <SelectItem value="single">Single Elimination</SelectItem>
                                <SelectItem value="double">Double Elimination</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-gray-300">Seeding Method</Label>
                            <Select 
                              value={tournamentForm.seeding_method} 
                              onValueChange={(v) => setTournamentForm({ ...tournamentForm, seeding_method: v })}
                            >
                              <SelectTrigger className="bg-rooam-gray-medium border-rooam-gray-light text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                                <SelectItem value="cross_pool">Cross-Pool (1A vs 2B)</SelectItem>
                                <SelectItem value="standing">By Standing</SelectItem>
                                <SelectItem value="random">Random</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        <p className="text-xs text-gray-500">
                          {tournamentForm.num_pools} pools × {tournamentForm.teams_advance_per_pool} advancing = {tournamentForm.num_pools * tournamentForm.teams_advance_per_pool} teams in bracket
                        </p>
                      </div>
                    )}
                    
                    <div>
                      <Label className="text-gray-300">
                        {tournamentForm.bracket_type === 'singlegame' 
                          ? 'Team Names (2 teams, comma separated)' 
                          : 'Team Names (comma separated, optional)'}
                      </Label>
                      <Input
                        value={tournamentForm.teams}
                        onChange={(e) => setTournamentForm({ ...tournamentForm, teams: e.target.value })}
                        className="bg-rooam-black border-rooam-gray-light text-white"
                        placeholder={tournamentForm.bracket_type === 'singlegame' ? "Home Team, Away Team" : "Team A, Team B, Team C..."}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={tournamentForm.auto_create_streams}
                          onChange={(e) => setTournamentForm({ ...tournamentForm, auto_create_streams: e.target.checked })}
                          className="rounded"
                        />
                        <span className="text-gray-300 text-sm">Auto-create live streams for each game</span>
                      </label>
                      {tournamentForm.auto_create_streams && (
                        <p className="text-xs text-rooam-blue ml-6">
                          Streams will be ready for broadcasting immediately
                        </p>
                      )}
                    </div>
                    <Button type="submit" className="w-full bg-rooam-blue hover:bg-rooam-blue-light text-black" disabled={loading}>
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Tournament'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Venue Manager Link */}
            <Card className="bg-gradient-to-r from-rooam-gray-medium to-rooam-gray-medium/80 border-rooam-gray-light mb-4 md:mb-6">
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="bg-rooam-blue/20 p-2 rounded-lg shrink-0">
                      <Building className="h-5 w-5 text-rooam-blue" />
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm sm:text-base">Venue Manager</p>
                      <p className="text-gray-400 text-xs sm:text-sm">Manage locations and courts</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    className="border-rooam-blue text-rooam-blue hover:bg-rooam-blue hover:text-black w-full sm:w-auto"
                    onClick={() => navigate('/venues')}
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    Venues ({venues.length})
                  </Button>
                </div>
              </CardContent>
            </Card>

            {tournaments.length === 0 ? (
              <Card className="bg-rooam-gray-medium border-rooam-gray-light">
                <CardContent className="p-8 sm:p-12 text-center">
                  <Trophy className="h-12 w-12 sm:h-16 sm:w-16 text-gray-500 mx-auto mb-4" />
                  <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">No Tournaments Yet</h3>
                  <p className="text-sm sm:text-base text-gray-400 mb-4">Create your first tournament bracket</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4 md:space-y-6">
                {tournaments.map((tournament) => (
                  <Card key={tournament.id} className="bg-rooam-gray-medium border-rooam-gray-light">
                    <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-6">
                      <div>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                          <CardTitle className="text-white text-base sm:text-lg">{tournament.name}</CardTitle>
                          <Badge className={getStatusBadge(tournament).className + " text-xs"}>
                            {getStatusBadge(tournament).label}
                          </Badge>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-400 mt-1">
                          {tournament.sport} • {getBracketTypeLabel(tournament.bracket_type)} • {tournament.num_teams} Teams
                          {tournament.bracket_type === 'poolplay' && tournament.num_pools > 0 && (
                            <span> • {tournament.num_pools} Pools</span>
                          )}
                        </p>
                        {tournament.champion_name && (
                          <p className="text-sm text-rooam-blue mt-1 flex items-center gap-1">
                            <Trophy className="h-4 w-4" />
                            Champion: {tournament.champion_name}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-rooam-blue text-rooam-blue hover:bg-rooam-blue hover:text-black"
                          onClick={() => handleAutoSchedule(tournament.id)}
                          disabled={loading || venues.length === 0}
                          title={venues.length === 0 ? "Add a venue first" : "Auto-schedule all matches"}
                        >
                          <Clock className="h-4 w-4 mr-1" />
                          Auto-Schedule
                        </Button>
                        {isAdmin() && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:bg-red-500/10"
                            onClick={() => handleDeleteTournament(tournament.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Pool Standings for Pool Play Tournaments */}
                      {tournament.bracket_type === 'poolplay' && tournament.pools?.length > 0 && (
                        <div className="mb-6">
                          <p className="text-sm font-semibold text-gray-300 mb-3">Pool Standings</p>
                          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {tournament.pools.map((pool) => {
                              const poolStandings = tournament.pool_standings?.filter(s => s.pool_id === pool.id) || [];
                              const sortedStandings = [...poolStandings].sort((a, b) => a.rank - b.rank);
                              
                              return (
                                <div key={pool.id} className="bg-rooam-black p-3 rounded-lg">
                                  <p className="text-xs text-rooam-blue font-semibold mb-2">{pool.name}</p>
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="text-gray-500">
                                        <th className="text-left pb-1">#</th>
                                        <th className="text-left pb-1">Team</th>
                                        <th className="text-center pb-1">W</th>
                                        <th className="text-center pb-1">L</th>
                                        <th className="text-right pb-1">+/-</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {sortedStandings.map((standing, idx) => (
                                        <tr 
                                          key={standing.team_name} 
                                          className={idx < tournament.teams_advance_per_pool ? 'text-green-400' : 'text-gray-400'}
                                        >
                                          <td className="py-0.5">{standing.rank || idx + 1}</td>
                                          <td className="py-0.5 truncate max-w-[80px]">{standing.team_name}</td>
                                          <td className="text-center py-0.5">{standing.wins}</td>
                                          <td className="text-center py-0.5">{standing.losses}</td>
                                          <td className="text-right py-0.5">{standing.point_diff > 0 ? '+' : ''}{standing.point_diff}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              );
                            })}
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            Green = Advances to bracket (Top {tournament.teams_advance_per_pool} per pool)
                          </p>
                        </div>
                      )}
                      
                      <p className="text-sm font-semibold text-gray-300 mb-3">
                        {tournament.bracket_type === 'singlegame' ? 'Matchup' : 
                         tournament.bracket_type === 'poolplay' ? 'Pool Games & Bracket' : 'Bracket'}
                        {tournament.auto_create_streams && (
                          <span className="ml-2 text-xs text-rooam-blue font-normal">
                            (Streams Ready)
                          </span>
                        )}
                      </p>
                      <div className="bg-rooam-black p-4 rounded-lg overflow-x-auto">
                        <div className="flex gap-8">
                          {/* Pool Play Tournament Display */}
                          {tournament.bracket_type === 'poolplay' ? (
                            <>
                              {/* Pool Matches */}
                              {tournament.pools?.map((pool) => (
                                <div key={pool.id} className="min-w-[220px]">
                                  <p className="text-xs text-rooam-blue mb-2 text-center font-semibold">{pool.name}</p>
                                  <div className="space-y-2">
                                    {tournament.matches?.filter(m => m.pool_id === pool.id).map(match => (
                                      <div 
                                        key={match.id} 
                                        className={`border rounded p-2 ${
                                          match.status === 'completed' 
                                            ? 'border-green-500/50 bg-green-500/5' 
                                            : 'border-rooam-gray-light'
                                        }`}
                                      >
                                        <div 
                                          className="cursor-pointer hover:opacity-80"
                                          onClick={() => {
                                            setSelectedTournament(tournament);
                                            setSelectedMatch(match);
                                            setScoreForm({ team1_score: match.team1_score, team2_score: match.team2_score });
                                            setShowScoreDialog(true);
                                          }}
                                        >
                                          <div className={`flex justify-between text-sm ${
                                            match.winner_name === match.team1_name ? 'text-green-400 font-semibold' : 'text-white'
                                          }`}>
                                            <span className="truncate max-w-[120px]">{match.team1_name}</span>
                                            <span>{match.team1_score}</span>
                                          </div>
                                          <div className={`flex justify-between text-sm ${
                                            match.winner_name === match.team2_name ? 'text-green-400 font-semibold' : 'text-white'
                                          }`}>
                                            <span className="truncate max-w-[120px]">{match.team2_name}</span>
                                            <span>{match.team2_score}</span>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                              
                              {/* Bracket Matches */}
                              {tournament.pool_play_complete && (
                                <div className="border-l-2 border-purple-500 pl-4">
                                  <p className="text-xs text-purple-400 mb-2 text-center font-semibold">Bracket</p>
                                  {Array.from(new Set(tournament.matches?.filter(m => m.stage === 'bracket').map(m => m.round) || [])).sort().map(round => (
                                    <div key={round} className="min-w-[220px] mb-4">
                                      <p className="text-xs text-gray-500 mb-2 text-center">Round {round - 1}</p>
                                      <div className="space-y-2">
                                        {tournament.matches?.filter(m => m.stage === 'bracket' && m.round === round).map(match => (
                                          <div 
                                            key={match.id} 
                                            className={`border rounded p-2 ${
                                              match.status === 'completed' 
                                                ? 'border-green-500/50 bg-green-500/5' 
                                                : 'border-purple-500/50'
                                            }`}
                                          >
                                            <div 
                                              className="cursor-pointer hover:opacity-80"
                                              onClick={() => {
                                                setSelectedTournament(tournament);
                                                setSelectedMatch(match);
                                                setScoreForm({ team1_score: match.team1_score, team2_score: match.team2_score });
                                                setShowScoreDialog(true);
                                              }}
                                            >
                                              <div className={`flex justify-between text-sm ${
                                                match.winner_name === match.team1_name ? 'text-green-400 font-semibold' : 'text-white'
                                              }`}>
                                                <span>{match.team1_name}</span>
                                                <span>{match.team1_score}</span>
                                              </div>
                                              <div className={`flex justify-between text-sm ${
                                                match.winner_name === match.team2_name ? 'text-green-400 font-semibold' : 'text-white'
                                              }`}>
                                                <span>{match.team2_name}</span>
                                                <span>{match.team2_score}</span>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          ) : (
                            /* Standard Tournament Display */
                            Array.from(new Set(tournament.matches?.map(m => m.round) || [])).sort().map(round => (
                              <div key={round} className="min-w-[220px]">
                                <p className="text-xs text-gray-500 mb-2 text-center">
                                  {tournament.bracket_type === 'roundrobin' || tournament.bracket_type === 'singlegame' 
                                    ? 'Matches' 
                                    : `Round ${round}`}
                                </p>
                                <div className="space-y-2">
                                  {tournament.matches?.filter(m => m.round === round).map(match => (
                                  <div 
                                    key={match.id} 
                                    className={`border rounded p-2 ${
                                      match.status === 'completed' 
                                        ? 'border-green-500/50 bg-green-500/5' 
                                        : match.stream_status === 'active'
                                        ? 'border-red-500 bg-red-500/10'
                                        : 'border-rooam-gray-light'
                                    }`}
                                  >
                                    <div 
                                      className="cursor-pointer hover:opacity-80"
                                      onClick={() => {
                                        setSelectedTournament(tournament);
                                        setSelectedMatch(match);
                                        setScoreForm({ team1_score: match.team1_score, team2_score: match.team2_score });
                                        setShowScoreDialog(true);
                                      }}
                                    >
                                      <div className={`flex justify-between text-sm ${
                                        match.winner_name === match.team1_name ? 'text-green-400 font-semibold' : 'text-white'
                                      }`}>
                                        <span>{match.team1_name}</span>
                                        <span>{match.team1_score}</span>
                                      </div>
                                      <div className={`flex justify-between text-sm ${
                                        match.winner_name === match.team2_name ? 'text-green-400 font-semibold' : 'text-white'
                                      }`}>
                                        <span>{match.team2_name}</span>
                                        <span>{match.team2_score}</span>
                                      </div>
                                    </div>
                                    
                                    {/* Schedule Info */}
                                    {(match.scheduled_date || match.venue_name) && (
                                      <div className="mt-2 pt-2 border-t border-rooam-gray-light space-y-1">
                                        {match.scheduled_date && (
                                          <p className="text-xs text-gray-400 flex items-center gap-1">
                                            <Calendar className="h-3 w-3" />
                                            {match.scheduled_date} {match.scheduled_time && `@ ${match.scheduled_time}`}
                                          </p>
                                        )}
                                        {match.venue_name && (
                                          <p className="text-xs text-gray-400 flex items-center gap-1">
                                            <MapPin className="h-3 w-3" />
                                            {match.venue_name}{match.court_name && ` - ${match.court_name}`}
                                          </p>
                                        )}
                                      </div>
                                    )}
                                    
                                    {/* Action Buttons */}
                                    <div className="mt-2 pt-2 border-t border-rooam-gray-light flex gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        data-testid={`schedule-match-btn-${match.id}`}
                                        className="flex-1 h-7 text-xs text-gray-400 hover:text-rooam-blue hover:bg-rooam-blue/10"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedTournament(tournament);
                                          setSelectedMatch(match);
                                          setScheduleForm({
                                            scheduled_date: match.scheduled_date || '',
                                            scheduled_time: match.scheduled_time || '',
                                            venue_id: match.venue_id || '',
                                            court_id: match.court_id || ''
                                          });
                                          setShowScheduleDialog(true);
                                        }}
                                      >
                                        <Clock className="h-3 w-3 mr-1" />
                                        Schedule
                                      </Button>
                                      {match.stream_id && (
                                        <Link 
                                          to={`/watch/${match.stream_id}`}
                                          className={`flex-1 flex items-center justify-center gap-1 text-xs py-1 px-2 rounded ${
                                            match.stream_status === 'active' 
                                              ? 'bg-red-500 text-white animate-pulse' 
                                              : 'bg-rooam-gray-light text-gray-300 hover:bg-rooam-blue hover:text-black'
                                          }`}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <Video className="h-3 w-3" />
                                          {match.stream_status === 'active' ? 'LIVE' : 'Watch'}
                                        </Link>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Update Score Dialog */}
            <Dialog open={showScoreDialog} onOpenChange={setShowScoreDialog}>
              <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light">
                <DialogHeader>
                  <DialogTitle className="text-white">Update Match Score</DialogTitle>
                </DialogHeader>
                {selectedMatch && (
                  <form onSubmit={handleUpdateMatchScore} className="space-y-4">
                    <div>
                      <Label className="text-gray-300">{selectedMatch.team1_name}</Label>
                      <Input
                        type="number"
                        value={scoreForm.team1_score}
                        onChange={(e) => setScoreForm({ ...scoreForm, team1_score: e.target.value })}
                        className="bg-rooam-black border-rooam-gray-light text-white"
                        min="0"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">{selectedMatch.team2_name}</Label>
                      <Input
                        type="number"
                        value={scoreForm.team2_score}
                        onChange={(e) => setScoreForm({ ...scoreForm, team2_score: e.target.value })}
                        className="bg-rooam-black border-rooam-gray-light text-white"
                        min="0"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-rooam-blue hover:bg-rooam-blue-light text-black" disabled={loading}>
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update Score'}
                    </Button>
                  </form>
                )}
              </DialogContent>
            </Dialog>

            {/* Schedule Match Dialog */}
            <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
              <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light" data-testid="schedule-match-dialog">
                <DialogHeader>
                  <DialogTitle className="text-white">Schedule Match</DialogTitle>
                </DialogHeader>
                {selectedMatch && (
                  <form onSubmit={handleScheduleMatch} className="space-y-4">
                    <div className="bg-rooam-black p-3 rounded-lg mb-4">
                      <p className="text-white font-medium text-center">
                        {selectedMatch.team1_name} vs {selectedMatch.team2_name}
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-300">Date</Label>
                        <Input
                          type="date"
                          value={scheduleForm.scheduled_date}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, scheduled_date: e.target.value })}
                          className="bg-rooam-black border-rooam-gray-light text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Time</Label>
                        <Input
                          type="time"
                          value={scheduleForm.scheduled_time}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, scheduled_time: e.target.value })}
                          className="bg-rooam-black border-rooam-gray-light text-white"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-gray-300">Venue</Label>
                      <Select 
                        value={scheduleForm.venue_id} 
                        onValueChange={(v) => setScheduleForm({ ...scheduleForm, venue_id: v, court_id: '' })}
                      >
                        <SelectTrigger className="bg-rooam-black border-rooam-gray-light text-white">
                          <SelectValue placeholder="Select venue" />
                        </SelectTrigger>
                        <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                          {venues.map((venue) => (
                            <SelectItem key={venue.id} value={venue.id}>
                              {venue.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {scheduleForm.venue_id && (
                      <div>
                        <Label className="text-gray-300">Court</Label>
                        <Select 
                          value={scheduleForm.court_id} 
                          onValueChange={(v) => setScheduleForm({ ...scheduleForm, court_id: v })}
                        >
                          <SelectTrigger className="bg-rooam-black border-rooam-gray-light text-white">
                            <SelectValue placeholder="Select court" />
                          </SelectTrigger>
                          <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                            {venues.find(v => v.id === scheduleForm.venue_id)?.courts?.map((court) => (
                              <SelectItem key={court.id} value={court.id}>
                                {court.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    {venues.length === 0 && (
                      <p className="text-sm text-yellow-400 flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        No venues available. <Link to="/venues" className="underline">Add a venue</Link>
                      </p>
                    )}
                    
                    <Button type="submit" className="w-full bg-rooam-blue hover:bg-rooam-blue-light text-black" disabled={loading}>
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Schedule'}
                    </Button>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="bg-rooam-gray-medium border-rooam-gray-light hover:border-rooam-blue transition-colors">
                <CardContent className="p-6">
                  <div className="text-center">
                    <Video className="h-12 w-12 text-rooam-blue mx-auto mb-3" />
                    <h3 className="text-xl font-bold text-white mb-1">Pay-Per-View</h3>
                    <p className="text-gray-400 text-sm mb-4">Single game access</p>
                    <p className="text-3xl font-bold text-rooam-blue mb-4">$4.99</p>
                    <Button 
                      className="w-full bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold"
                      onClick={() => handlePayment('ppv_game')}
                    >
                      Buy Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-rooam-gray-medium border-rooam-gray-light hover:border-rooam-blue transition-colors">
                <CardContent className="p-6">
                  <div className="text-center">
                    <Calendar className="h-12 w-12 text-purple-400 mx-auto mb-3" />
                    <h3 className="text-xl font-bold text-white mb-1">Event Pass</h3>
                    <p className="text-gray-400 text-sm mb-4">Access to single event</p>
                    <p className="text-3xl font-bold text-purple-400 mb-4">$9.99</p>
                    <Button 
                      className="w-full bg-purple-500 hover:bg-purple-400 text-white font-semibold"
                      onClick={() => handlePayment('event_pass')}
                    >
                      Buy Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-rooam-gray-medium border-rooam-blue relative overflow-hidden">
                <div className="absolute top-2 right-2">
                  <Badge className="bg-rooam-blue text-black">Popular</Badge>
                </div>
                <CardContent className="p-6">
                  <div className="text-center">
                    <CreditCard className="h-12 w-12 text-rooam-blue mx-auto mb-3" />
                    <h3 className="text-xl font-bold text-white mb-1">Monthly</h3>
                    <p className="text-gray-400 text-sm mb-4">Unlimited access</p>
                    <p className="text-3xl font-bold text-rooam-blue mb-4">$19.99<span className="text-sm text-gray-400">/mo</span></p>
                    <Button 
                      className="w-full bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold"
                      onClick={() => handlePayment('monthly_sub')}
                    >
                      Subscribe
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-rooam-gray-medium border-rooam-gray-light hover:border-green-500 transition-colors">
                <CardContent className="p-6">
                  <div className="text-center">
                    <Trophy className="h-12 w-12 text-green-400 mx-auto mb-3" />
                    <h3 className="text-xl font-bold text-white mb-1">Annual</h3>
                    <p className="text-gray-400 text-sm mb-4">Best value - save 37%</p>
                    <p className="text-3xl font-bold text-green-400 mb-4">$149.99<span className="text-sm text-gray-400">/yr</span></p>
                    <Button 
                      className="w-full bg-green-500 hover:bg-green-400 text-white font-semibold"
                      onClick={() => handlePayment('annual_sub')}
                    >
                      Subscribe
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Streams Tab */}
          <TabsContent value="streams">
            <Card className="bg-rooam-gray-medium border-rooam-gray-light">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Radio className="h-5 w-5 text-red-500" />
                    Event Stream Status
                  </CardTitle>
                  <Button size="sm" variant="outline" className="border-rooam-gray-light text-gray-300 text-xs" onClick={fetchEventStreams}>
                    <RefreshCw className="h-3 w-3 mr-1" /> Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {eventStreams.length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    <Radio className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No events found</p>
                    <p className="text-xs mt-1">Create events and configure streaming in the Admin Dashboard</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {eventStreams.map(event => (
                      <div key={event.id || event.event_id} className="bg-rooam-black rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <div className="text-sm font-semibold text-white">{event.name}</div>
                            <div className="text-xs text-zinc-500">{event.sport} {event.date ? `| ${new Date(event.date).toLocaleDateString()}` : ''}</div>
                          </div>
                          <Badge className={event.stream_config?.stream_mode === 'per_court' ? 'bg-cyan-600 text-white' : 'bg-zinc-700 text-zinc-300'}>
                            {event.stream_config?.stream_mode === 'per_court' ? 'Tournament' : 'Per Game'}
                          </Badge>
                        </div>
                        
                        {event.court_streams?.length > 0 ? (
                          <div className="space-y-2">
                            {event.court_streams.map(court => (
                              <div key={court.court_stream_id} className={`flex items-center justify-between p-2 rounded-lg ${
                                court.is_live ? 'bg-emerald-900/20 border border-emerald-800/40' : 'bg-zinc-800/50'
                              }`}>
                                <div className="flex items-center gap-2">
                                  {court.is_live ? (
                                    <Wifi className="h-3.5 w-3.5 text-emerald-400" />
                                  ) : (
                                    <WifiOff className="h-3.5 w-3.5 text-zinc-600" />
                                  )}
                                  <span className="text-sm text-zinc-300">{court.court_name}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  {court.current_game_id && (
                                    <Badge className="bg-red-600 text-white text-[10px]">Game Active</Badge>
                                  )}
                                  <Badge className={court.is_live ? 'bg-emerald-600 text-white text-[10px]' : 'bg-zinc-700 text-zinc-400 text-[10px]'}>
                                    {court.is_live ? 'LIVE' : 'Offline'}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-xs text-zinc-600 py-2">
                            {event.stream_config?.stream_mode === 'per_court' 
                              ? 'No courts configured yet' 
                              : 'Per-game streams are configured individually'}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Dashboard;
